import { useMutation } from '@tanstack/react-query';
import type { ScmProviderId } from '@/types/scm-provider';
import { inferScmProviderFromRepoUrl } from '@/types/scm-provider';
import { scmGet, scmPost } from './scmClient';
import type { Build } from './getBuildList';

const COMPARE_POLL_MS = 3000;
const COMPARE_MAX_WAIT_MS = 10 * 60 * 1000;

type BuildWithMeta = Build & {
  branch: string;
  commitHash: string;
};

export interface CompareBuildRequest {
  repositoryUrl: string;
  buildA: BuildWithMeta;
  buildB: BuildWithMeta;
  /** Mongo repository id — used to resolve scanId from buildId for Test Optimization */
  repoId?: string;
  provider?: ScmProviderId;
}

export interface BuildScanMeta {
  scanId: string;
  buildId?: number;
  buildNumber?: string;
  commitId?: string;
}

/** One row for Test Optimization Methods tab (from POST /api/scm/builds/compare). */
export interface TestOptimizationMethodDto {
  changeType: 'added' | 'modified' | 'deleted';
  category: 'methods' | 'functions' | string;
  entityName: string;
  entityType: string;
  entityKey: string;
  entityId: string;
  filePath: string;
  lineStart: number;
  lineEnd: number;
  severity: string;
  score: number;
  fanIn: number;
  fanOut: number;
  coveredBy: string[];
  entity: Record<string, unknown>;
  matchedInScanEntities?: boolean;
  diffIndex?: number;
  matchIndex?: number;
}

export interface EntityStats {
  added: number;
  modified: number;
  deleted: number;
  unchanged: number;
}

export interface CompareEntities {
  methods: EntityStats;
  functions: EntityStats;
  classes: EntityStats;
  branches: EntityStats;
  loops: EntityStats;
}

export interface CompareSummary {
  totalFiles: number;
  totalLinesAdded: number;
  totalLinesDeleted: number;
  totalLinesModified: number;
  totalAdded: number;
  totalModified: number;
  totalDeleted: number;
}

export interface CompareFile {
  filePath: string;
  fileName?: string;
  linesAdded: number;
  linesDeleted: number;
  linesModified?: number;
  status?: string;
  addedLineNumbers?: number[];
  deletedLineNumbers?: number[];
  modifiedLineNumbers?: [number, number][];
  entities?: Record<
    string,
    {
      added: { entityName: string; entityType: string }[];
      modified: { entityName: string; entityType: string }[];
      deleted: { entityName: string; entityType: string }[];
      unchanged: { entityName: string; entityType: string }[];
    }
  >;
  old_content?: string;
  new_content?: string;
}

export interface CompareResponse {
  success: boolean;
  buildA: string;
  buildB: string;
  summary: CompareSummary;
  entities: CompareEntities;
  files: CompareFile[];
  scanA?: BuildScanMeta;
  scanB?: BuildScanMeta;
  testOptimizationMethods?: TestOptimizationMethodDto[];
  testOptimizationSummary?: {
    diffEntityCount: number;
    methodsRowCount: number;
    diffByChangeType: { added: number; modified: number; deleted: number };
    rowsByChangeType: { added: number; modified: number; deleted: number };
  };
}

export interface CompareJobAccepted {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
}

export interface CompareJobStatus extends CompareJobAccepted {
  result?: CompareResponse;
  error?: string;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface CompareBuildApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: {
    summary: Record<string, unknown>;
    fileLevelComparison: unknown[];
    diffServiceData: CompareResponse;
    scanA: {
      scanId: string;
      buildId: number;
      buildNumber: string;
      commitId: string;
      selectedAt: string;
    };
    scanB: {
      scanId: string;
      buildId: number;
      buildNumber: string;
      commitId: string;
      selectedAt: string;
    };
    derivedCoverage?: Record<string, unknown>;
  };
}

const pollCompareJob = async (
  provider: ScmProviderId,
  patToken: string,
  jobId: string,
): Promise<CompareResponse> => {
  const started = Date.now();
  while (Date.now() - started < COMPARE_MAX_WAIT_MS) {
    const job = await scmGet<CompareJobStatus>(
      provider,
      `/api/scm/builds/compare/jobs/${jobId}`,
      patToken,
    );
    if (job.status === 'completed' && job.result) {
      return job.result;
    }
    if (job.status === 'failed') {
      throw new Error(job.error || 'Compare failed');
    }
    await sleep(COMPARE_POLL_MS);
  }
  throw new Error('Compare timed out while waiting for the report');
};

const compareBuildDiffFn = async (
  params: CompareBuildRequest,
  patToken: string,
): Promise<CompareResponse> => {
  const provider =
    params.provider ?? inferScmProviderFromRepoUrl(params.repositoryUrl);

  const accepted = await scmPost<CompareJobAccepted>(
    provider,
    '/api/scm/builds/compare',
    patToken,
    {
      repositoryUrl: params.repositoryUrl,
      buildA: params.buildA,
      buildB: params.buildB,
      repoId: params.repoId,
    },
  );

  if (!accepted?.jobId) {
    throw new Error('Compare job was not created');
  }

  return pollCompareJob(provider, patToken, accepted.jobId);
};

export const useCompareBuildDiff = (
  patToken: string,
  provider?: ScmProviderId,
) => {
  return useMutation<CompareResponse, Error, CompareBuildRequest>({
    mutationFn: params =>
      compareBuildDiffFn(
        {
          ...params,
          provider: params.provider ?? provider,
        },
        patToken,
      ),
  });
};
